/**
 * Admin service — API calls for the admin console.
 * Uses the shared apiClient, following the same pattern as subscriptionService.js.
 */
import apiClient from './apiClient';

/**
 * Fetches aggregate counts for the admin overview.
 * @returns {{ totalUsers, activeSubscriptions, trials, expired }}
 */
export async function fetchStats() {
  const { data } = await apiClient.get('/admin/stats');
  return data.data;
}

/**
 * Fetches a paginated list of users with role and subscription info.
 * @param {number} page — 1-based page number
 * @param {number} [limit] — page size; the server caps it and falls back to its default
 * @returns {{ users, total, page, totalPages }}
 */
export async function fetchUsers(page = 1, limit) {
  const query = new URLSearchParams({ page: String(page) });
  if (limit) query.set('limit', String(limit));
  const { data } = await apiClient.get(`/admin/users?${query.toString()}`);
  return data.data;
}

/**
 * Changes a user's role.
 * @param {string} userId
 * @param {string} role — 'ADMIN' | 'ENGINEER'
 */
export async function updateUserRole(userId, role) {
  const { data } = await apiClient.patch(`/admin/users/${userId}/role`, { role });
  return data.data;
}

/**
 * Manually overrides a user's subscription.
 * @param {string} userId
 * @param {{ status?, planCode?, endsAt?, trialEnds? }} fields
 */
export async function updateUserSubscription(userId, fields) {
  const { data } = await apiClient.patch(`/admin/users/${userId}/subscription`, fields);
  return data.data;
}

/**
 * Suspends, deactivates, or reactivates a user account.
 * @param {string} userId
 * @param {string} accountStatus — 'ACTIVE' | 'SUSPENDED' | 'DEACTIVATED'
 */
export async function updateUserAccount(userId, accountStatus, reason) {
  const { data } = await apiClient.patch(`/admin/users/${userId}/account`, {
    accountStatus,
    reason,
  });
  return data.data;
}
